import type { PostShowingSurveyResponse } from "api/post-showing-survey/types";
import type { QuestionType } from "shared/types";

// === Enums ===

export enum ShowingStatus {
  Reserved = "reserved",
  Pending = "pending",
  Confirmed = "confirmed",
  LeadConfirmed = "lead_confirmed",
  InProgress = "in_progress",
  Completed = "completed",
  NoShow = "no_show",
  Cancelled = "canceled",
  Expired = "expired",
  Ongoing = "ongoing"
}

export enum ShowingType {
  Guided = "guided",
  SelfGuided = "self_guided"
}

export enum ShowingFeedback {
  VeryInterested = "very_interested",
  Interested = "interested",
  Neutral = "neutral",
  NotInterested = "not_interested"
}

// === Scheduling Settings ===

export type NotificationChannel = "email" | "sms";

export interface SchedulingSettings {
  id: number;
  partner_id: number;
  showing_type: ShowingType;
  duration_minutes: number;
  available_days_in_future: number | null;
  require_confirmation: boolean;
  timezone: string;
  minimum_scheduling_notice_minutes: number;
  max_visits_per_unit_daily: number | null;
  max_visits_per_unit_weekly: number | null;
  only_schedule_assigned_user: boolean;
  intelligent_scheduling_enabled: boolean;
  drive_time_buffer_percent: number | null;
  checkin_link_lead_time_minutes: number | null;
  checkout_reminder_minutes_before_end: number | null;
  require_geolocation_validation: boolean;
  geolocation_validation_radius_miles: number;
  require_serial_number_validation: boolean;
  no_show_alert_minutes: number | null;
  auto_mark_no_show: boolean;
  send_confirmation_before_minutes: number | null;
  notification_channels: NotificationChannel[];
  notify_tenant_enabled: boolean;
  tenant_notification_channels: NotificationChannel[];
  group_showings_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export type UpdateSchedulingSettingsPayload = Partial<
  Omit<
    SchedulingSettings,
    "id" | "partner_id" | "showing_type" | "created_at" | "updated_at"
  >
>;

// === Showing Settings ===

export interface BaseAddress {
  address1: string;
  city: string;
  state: string;
  zip: string;
}

export interface AddressEntity {
  address1: string;
  address2: string | null;
  city: string;
  state_or_province: string;
  postal_code: string;
  country: string;
  lat: number | null;
  lng: number | null;
  one_line_address: string;
}

export interface ShowingSettings {
  id: number;
  partner_user_id: number;
  buffer_minutes: number;
  scheduling_limit_days: number | null;
  timezone: string;
  default_reminder_minutes: number | null;
  base_address: AddressEntity | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateShowingSettingsPayload {
  buffer_minutes?: number;
  scheduling_limit_days?: number | null;
  timezone?: string;
  default_reminder_minutes?: number | null;
  base_address?: BaseAddress;
}

// === Showing Eligible Properties ===

export interface ShowingEligiblePropertyAddress {
  address1: string | null;
  address2: string | null;
  city: string | null;
  state_or_province: string | null;
  postal_code: string | null;
}

export interface ShowingEligibleProperty {
  property_id: number;
  name: string;
  property_group_name: string | null;
  address: ShowingEligiblePropertyAddress | null;
  eligible: boolean;
}

export interface ShowingEligiblePropertiesResponse {
  properties: ShowingEligibleProperty[];
}

export interface ReplaceShowingEligiblePropertiesPayload {
  eligible_property_ids: number[];
}

// === Weekly Availability ===

export interface AvailabilityRule {
  id: number;
  partner_user_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

export interface ReplaceAvailabilityPayload {
  availabilities: Omit<AvailabilityRule, "id" | "partner_user_id">[];
}

// === Blocked Days ===

export interface BlockedDay {
  id: number;
  partner_user_id: number;
  blocked_date: string;
  reason: string | null;
}

export interface AddBlockedDayPayload {
  blocked_date: string;
  reason?: string;
}

// === Date Overrides ===

export interface TimeWindow {
  start_time: string;
  end_time: string;
}

export interface DateOverride {
  id: number;
  partner_user_id: number;
  override_date: string;
  unavailable_all_day: boolean;
  start_time?: string;
  end_time?: string;
  created_at: string;
}

export interface SetDateOverridePayload {
  override_date: string;
  unavailable_all_day?: boolean;
  windows?: TimeWindow[];
}

export interface RemoveDateOverridePayload {
  override_date: string;
}

// === Calendar ===

export interface CalendarParams {
  start_date: string;
  end_date: string;
  timezone?: string;
  property_ids?: number[];
  unit_ids?: number[];
  partner_user_ids?: number[];
  include_unassigned?: boolean;
  all_assignees?: boolean;
  statuses?: ShowingStatus[];
  showing_types?: ShowingType[];
}

export interface ExternalEvent {
  title: string;
  email: string;
  start_time: string;
  end_time: string;
  provider: string;
  all_day: boolean;
}

export interface CalendarResponse {
  timezone: string;
  showings: Showing[];
  group_showings?: GroupShowing[];
  external_events: ExternalEvent[];
  blocked_days: BlockedDay[];
  date_overrides: DateOverride[];
  weekly_availability: AvailabilityRule[];
}

// === Nested Objects ===

export interface PartnerUserRef {
  id: number;
  name: string;
  email: string;
}

export interface AIAgentRef {
  id: number;
  name: string;
}

export enum ShowingBookedVia {
  Call = "call",
  Sms = "sms",
  Email = "email"
}

export interface PropertyRef {
  id: number;
  name: string;
  address: string | null;
}

export interface UnitRef {
  id: number;
  title: string;
}

export interface LeadRef {
  id: number;
  first_name: string;
  last_name: string;
  primary_email: string;
  primary_phone: string | null;
}

// === Showings ===

export interface Showing {
  id: number;
  showing_type: ShowingType;
  status: ShowingStatus;
  start_time: string;
  end_time: string;
  timezone: string | null;
  desired_move_in_date: string | null;
  message: string | null;
  feedback: ShowingFeedback | null;
  cancelled_by_type: string | null;
  cancelled_by_id: number | null;
  cancellation_reason: string | null;
  cancelled_at: string | null;
  confirmed_at: string | null;
  reserved_at: string | null;
  reservation_expires_at: string | null;
  confirmation_expires_at: string | null;
  rescheduled_from_id: number | null;
  reschedule_count: number;
  reschedule_pending: boolean;
  reschedule_requested_at: string | null;
  reschedule_previous_start_time: string | null;
  reschedule_previous_end_time: string | null;
  checked_in_at: string | null;
  checkin_link_sent_at: string | null;
  partner_user: PartnerUserRef | null;
  booked_by_partner_user: PartnerUserRef | null;
  booked_by_ai_agent: AIAgentRef | null;
  booked_via: ShowingBookedVia | null;
  property: PropertyRef;
  unit: UnitRef | null;
  lead: LeadRef | null;
  lockbox_code: string | null;
  tenant_contact_missing: boolean;
  party_size: number;
  // Always false outside a group-showing roster.
  over_capacity_in_group: boolean;
  created_at: string;
  updated_at: string;
}

export enum GroupShowingStatus {
  Scheduled = "scheduled",
  Cancelled = "cancelled"
}

export interface GroupShowing {
  id: number;
  status: GroupShowingStatus;
  showing_type: ShowingType;
  start_time: string;
  end_time: string;
  timezone: string;
  capacity: number;
  max_party_size: number | null;
  // Total people expected, not the number of registration rows.
  booked_count: number;
  spots_left: number;
  full: boolean;
  over_capacity: boolean;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  partner_user: PartnerUserRef | null;
  property: PropertyRef;
  unit: UnitRef | null;
  created_at: string;
  updated_at: string;
}

export interface GroupShowingRoster extends GroupShowing {
  attendees: Showing[];
  notes: ShowingNote[];
}

export interface CreateGroupShowingPayload {
  property_id: number;
  unit_id: number;
  partner_user_id?: number;
  start_time: string;
  end_time: string;
  capacity: number;
  max_party_size?: number;
}

export interface AddGroupShowingAttendeePayload {
  lead_id?: number;
  lead_name?: string;
  lead_email?: string;
  lead_phone?: string;
  allow_over_capacity?: boolean;
  party_size?: number;
}

export interface RemoveGroupShowingAttendeePayload {
  showing_id: number;
  cancellation_reason?: string;
}

export interface CancelGroupShowingPayload {
  cancellation_reason?: string;
}

export interface PropertyContact {
  company_name: string;
  phone: string;
}

export interface ShowingNote {
  id: number;
  body: string;
  partner_user: PartnerUserRef;
  created_at: string;
}

export interface ShowingPropertyAddress {
  address1: string;
  address2?: string | null;
  city: string;
  state_or_province: string;
  postal_code: string;
  country?: string;
  lat?: number | null;
  lng?: number | null;
  one_line_address: string;
}

export interface PrescreeningResultAnswer {
  question_id: string;
  name: string;
  question: string | null;
  type: QuestionType;
  // A Checkbox answer is the list of ticked options.
  answer: string | string[];
}

export interface PrescreeningResultCreditScore {
  score: number | null;
  score_range: string | null;
  score_unspecified: boolean;
}

export interface PrescreeningResultIncome {
  income_cents: number | null;
  income_range: string | null;
}

export interface PrescreeningResults {
  question_answers: PrescreeningResultAnswer[];
  credit_score: PrescreeningResultCreditScore | null;
  income: PrescreeningResultIncome | null;
}

export interface ShowingDetail extends Showing {
  property_address: ShowingPropertyAddress;
  property_contacts: PropertyContact | null;
  notes: ShowingNote[];
  post_showing_survey_response: PostShowingSurveyResponse | null;
  prescreening_results: PrescreeningResults | null;
}

// === Showing Payloads ===

export interface ReserveSlotPayload {
  property_id: number;
  start_time: string;
  end_time: string;
  lead_id?: number;
  lead_email?: string;
  unit_id?: number;
  showing_type?: ShowingType;
}

export interface BookFromReservationPayload {
  showing_id: number;
  lead_id?: number;
  lead_name?: string;
  lead_email?: string;
  lead_phone?: string;
  desired_move_in_date?: string;
  message?: string;
  booked_by_partner_user_id?: number;
}

export interface BookDirectPayload {
  property_id: number;
  start_time: string;
  end_time: string;
  unit_id?: number;
  lead_id?: number;
  lead_name?: string;
  lead_email?: string;
  lead_phone?: string;
  desired_move_in_date?: string;
  message?: string;
  showing_type?: ShowingType;
  booked_by_partner_user_id?: number;
  assignee_id?: number;
}

export interface CancelShowingPayload {
  cancellation_reason?: string;
}

export interface RescheduleShowingPayload {
  new_start_time: string;
  new_end_time: string;
}

export interface RescheduleGroupShowingPayload {
  new_start_time: string;
  new_end_time: string;
}

export interface AddFeedbackPayload {
  feedback: ShowingFeedback;
}

export interface ReassignShowingPayload {
  partner_user_id: number;
}

export interface AddNotePayload {
  note_content: string;
}

// CRM-1163: manual "Send lockbox code" action — pushes the lockbox code to a
// self-guided lead who hasn't tapped their check-in link yet, and marks the
// showing checked-in as a side effect.
export type SendLockboxCodeDisclosure =
  | "codebox_full_day"
  | "rently_full_day"
  | "rently_one_time_3h";

export interface SendLockboxCodePayload {
  channels: NotificationChannel[];
}

export interface SendLockboxCodeResponse {
  showing: Showing;
  disclosure: SendLockboxCodeDisclosure | null;
  // True when the showing was still marked checked-in but the SMS/email send
  // itself failed — the code must be relayed to the lead out of band.
  delivery_failed: boolean;
}

// === Availability Slots ===

export interface AvailabilitySlot {
  available_count: number;
  date: string;
  start_time: string;
  end_time: string;
  partner_user_ids: number[];
}

export interface AvailabilitySlotParams {
  property_id: number;
  unit_id?: number | null;
  lead_id?: number;
  partner_user_id?: number;
  showing_type?: ShowingType;
}

// === Availability Overview (PX unit "Showing availability" tab) ===

export type AvailabilityOverviewSlotState = "available" | "unavailable";

/** Detail-free category explaining an unavailable slot; null when available. */
export type AvailabilityOverviewUnavailableReason =
  | "outside_hours"
  | "conflict"
  | "buffer";

export interface AvailabilityOverviewSlot {
  start_time: string;
  end_time: string;
  state: AvailabilityOverviewSlotState;
  reason: AvailabilityOverviewUnavailableReason | null;
}

export interface AvailabilityOverviewShowing {
  id: number;
  start_time: string;
  end_time: string;
  status: string;
}

export interface AvailabilityOverviewDay {
  date: string;
  slots: AvailabilityOverviewSlot[];
  showings: AvailabilityOverviewShowing[];
}

export interface AvailabilityOverviewTeamMember {
  partner_user: PartnerUserRef;
  days: AvailabilityOverviewDay[];
}

export interface AvailabilityOverviewDayBand {
  /** "yyyy-MM-dd" */
  date: string;
  /** Property-local hours of that day's display band, e.g. 9 and 23 (or 24). */
  band_start_hour: number;
  band_end_hour: number;
}

export interface AvailabilityOverviewResponse {
  timezone: string;
  slot_minutes: number;
  /** One band per date in the range — each day spans only its own hours. */
  day_bands: AvailabilityOverviewDayBand[];
  team_members: AvailabilityOverviewTeamMember[];
}

export interface AvailabilityOverviewParams {
  property_id: number;
  unit_id: number;
  start_date: string;
  end_date: string;
}

// === Showing Rotation ===

export interface ShowingRotationSlot {
  partner_user_id: number;
  name: string;
  position: number;
  is_next: boolean;
  paused: boolean;
  pause_reason: string | null;
  showings_today: number;
  showings_this_week: number;
}

// === List Showings ===

export enum ShowingsListTab {
  Past = "past",
  Today = "today",
  Upcoming = "upcoming"
}

export type ShowingsListSort = "asc" | "desc";

export interface ShowingTabCounts {
  past: number;
  today: number;
  upcoming: number;
}

export interface ListShowingsParams {
  property_id?: number;
  unit_id?: number;
  partner_user_id?: number;
  include_unassigned?: boolean;
  statuses?: ShowingStatus[];
  showing_types?: ShowingType[];
  tab?: ShowingsListTab;
  timezone?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  sort?: ShowingsListSort;
  page?: number;
  per_page?: number;
}

export interface ListShowingsResponse {
  items: Showing[];
  current_page: number;
  page_count: number;
  total_count: number;
  has_more: boolean;
  counts: ShowingTabCounts;
}

// === Lead Section Showings (lean payload from GET /leads/:id/showings) ===

export interface LeadShowing {
  id: number;
  status: ShowingStatus;
  showing_type: ShowingType;
  feedback: ShowingFeedback | null;
  start_time: string;
  end_time: string;
  timezone: string | null;
  reschedule_pending: boolean;
  reschedule_requested_at: string | null;
  reschedule_previous_start_time: string | null;
  reschedule_previous_end_time: string | null;
  partner_user: PartnerUserRef | null;
  property: PropertyRef;
  unit: UnitRef | null;
  property_address: ShowingPropertyAddress | null;
  post_showing_survey_response: PostShowingSurveyResponse | null;
}

export interface LeadShowingsResponse {
  showings: LeadShowing[];
  total_count: number;
  available_channels?: ShowingInviteChannel[];
  showing_scheduling_enabled?: boolean;
  has_property_and_unit?: boolean;
  active_invite?: ActiveShowingInvite | null;
}

// "both" = one invite delivered over both channels (workflow-sent invites).
export type ShowingInviteChannel = "email" | "sms" | "both";

export interface ActiveShowingInvite {
  sent_at: string;
  channel: ShowingInviteChannel;
}

export interface SendShowingInvitePayload {
  channel?: ShowingInviteChannel;
  destination_contact_method_id?: number;
}

export interface ShowingInvite {
  id: string;
  lead_id: string;
  channel: ShowingInviteChannel;
  destination: string;
  invite_url: string;
  expires_at: string;
  sent_at: string;
}
